import { typeWithParameters } from '@angular/compiler/src/render3/util';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, Subject } from 'rxjs';
import { RestDeviceDocumentationService } from 'src/app/service/rest/device-documentation/rest-device-documentation.service';
import { PageService } from 'src/app/service/utils/page.service';
import { TranslateFirebaseService } from 'src/app/service/utils/translate-firebase.service';
import {
  CustomContentType,
  Document,
  DocumentStatus,
  ImageMedia,
  ImageMediaPost,
  VideoLinks,
  VideoMedia,
  VideoMediaPost,
} from './device-documentation.model';

@Injectable()
export class DeviceDocumentationRepositoryService {
  private _articleDocuments: Document[];
  private _serialNumberDocuments: Document[];
  private _imageMedia: ImageMedia[] = [];
  private _videoMedia: VideoMedia[] = [];
  private _imgSrcs: string[] = [];
  private _videoSrcs: VideoLinks[] = [];
  private _dataSourceDocuments: MatTableDataSource<Document>;
  public targetMediaUploadType: CustomContentType;

  private refreshMedia = new Subject<any>();
  refreshMedia$ = this.refreshMedia.asObservable();

  constructor(
    private documentationRest: RestDeviceDocumentationService,
    private pageService: PageService,
    private translate: TranslateFirebaseService
  ) {}

  public get articleDocuments(): Document[] {
    return this._articleDocuments;
  }
  public set articleDocuments(value: Document[]) {
    this._articleDocuments = value;
  }

  public get videoMedia(): VideoMedia[] {
    return this._videoMedia;
  }
  public set videoMedia(value: VideoMedia[]) {
    this._videoMedia = value;
  }

  public get imageMedia(): ImageMedia[] {
    return this._imageMedia;
  }
  public set imageMedia(value: ImageMedia[]) {
    this._imageMedia = value;
  }

  public get dataSourceDocuments(): MatTableDataSource<Document> {
    return this._dataSourceDocuments;
  }
  public set dataSourceDocuments(value: MatTableDataSource<Document>) {
    this._dataSourceDocuments = value;
  }

  public get imgSrcs(): string[] {
    return this._imgSrcs;
  }
  public set imgSrcs(value: string[]) {
    this._imgSrcs = value;
  }

  public get videoSrcs(): VideoLinks[] {
    return this._videoSrcs;
  }
  public set videoSrcs(value: VideoLinks[]) {
    this._videoSrcs = value;
  }

  public get serialNumberDocuments(): Document[] {
    return this._serialNumberDocuments;
  }
  public set serialNumberDocuments(value: Document[]) {
    this._serialNumberDocuments = value;
  }

  getDocumentsByArticle(articleId: number) {
    this.documentationRest.getDocumentsByArticleId(articleId).subscribe(
      (result: Document[]) => {
        this.articleDocuments = result;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  getDocumentsBySerialNumber(serialNumberId: number) {
    this.documentationRest.getDocumentsBySerialNumber(serialNumberId).subscribe(
      (result: Document[]) => {
        this.serialNumberDocuments = result;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  getDocumentsByArticleAndLanguage(
    articleId: number,
    languageId: string
  ): Observable<any> {
    return this.documentationRest.getAllDocumentsByArticleIdAndLanguageId(
      articleId,
      languageId
    );
  }

  getMediaByArticleId(articleId: number) {
    this.documentationRest
      .getMediaByArticleId(articleId)
      .subscribe((result: ImageMedia[] | VideoMedia[]) => {
        this.imageMedia = [];
        this.imgSrcs = [];
        this.videoMedia = [];
        this.videoSrcs = [];
        result.forEach((element: any) => {
          if (element.contentType.toString().startsWith('image/'))
            this.imageMedia.push(element);
          else if (element.contentType.toString().startsWith('video/'))
            this.videoMedia.push(element);
          this.imgSrcs = this.imageMedia.map((media) => media.storageUri);
          this.videoSrcs = this.videoMedia.map(
            (video) => new VideoLinks(video.externalLink, video.thumbnailLink)
          );
        });
      });
  }

  getMediaByArticleIdAndLanguageId(articleId: number,languageId:string = 'en') {
    this.documentationRest
      .getMediaByArticleIdandLanguageId(articleId,languageId)
      .subscribe((result: ImageMedia[] | VideoMedia[]) => {
        this.imageMedia = [];
        this.imgSrcs = [];
        this.videoMedia = [];
        this.videoSrcs = [];
        result.forEach((element: any) => {
          if (element.contentType.toString().startsWith('image/'))
            this.imageMedia.push(element);
          else if (element.contentType.toString().startsWith('video/'))
            this.videoMedia.push(element);
          this.imgSrcs = this.imageMedia.map((media) => media.storageUri);
          this.videoSrcs = this.videoMedia.map(
            (video) => new VideoLinks(video.externalLink, video.thumbnailLink)
          );
        });
      });
  }

  uploadNewVideoMediaExternal(media: VideoMediaPost): Observable<VideoMedia> {
    return this.documentationRest.uploadNewVideoMediaExternal(media);
  }

  uploadNewVideoMedia(media: FormData): Observable<VideoMedia> {
    return this.documentationRest.uploadNewVideoMedia(media);
  }

  uploadNewImageMedia(media: FormData): Observable<ImageMedia> {
    return this.documentationRest.uploadNewImageMedia(media);
  }

  deleteImageMediaBySrc(src: string) {
    const mediaToDeleteId = this.imageMedia.find(
      (media) => media.storageUri == src
    ).id;
    this.documentationRest.deleteMedia(mediaToDeleteId).subscribe(
      (result: any) => {
        const index = this.imageMedia.findIndex(
          (media) => media.id === mediaToDeleteId
        );
        this.imageMedia.splice(index, 1);
        const srcIndex = this.imgSrcs.findIndex((media) => media === src);
        this.imgSrcs.splice(srcIndex, 1);
        this.pageService.openSnackBar(
          this.translate.getTranslation('backoffice_general_delete_success'),
          'Ok'
        );
      },
      (error) => {
        console.log(error);
        this.pageService.openErrorSnackBar(
          this.translate.getTranslation('backoffice_general_delete_failed'),
          'Ok'
        );
      }
    );
  }

  deleteVideoMediaBySrc(src: string) {
    const mediaToDeleteId = this.videoMedia.find(
      (media) => {
        if (media.externalLink) {
          return media.externalLink == src;
        }
        return media.storageUri == src;
      }
    ).id;
    this.documentationRest.deleteMedia(mediaToDeleteId).subscribe(
      (result: any) => {
        const index = this.videoMedia.findIndex(
          (media) => media.id === mediaToDeleteId
        );
        this.videoMedia.splice(index, 1);
        const srcIndex = this.videoSrcs.findIndex(
          (media) => media.externalLink === src
        );
        this.videoSrcs.splice(srcIndex, 1);
        this.pageService.openSnackBar(
          this.translate.getTranslation('backoffice_general_delete_success'),
          'Ok'
        );
      },
      (error) => {
        console.log(error);
        this.pageService.openErrorSnackBar(
          this.translate.getTranslation('backoffice_general_delete_failed'),
          'Ok'
        );
      }
    );
  }

  updateDocOrder(articleCode: number, orderArray: Document[]) {
    this.documentationRest
      .updateDocumentOrder(articleCode, orderArray)
      .subscribe(
        (result) => {},
        (error) => {
          console.log(error);
        }
      );
  }

  updateMediaOrder(articleCode: number, orderArray: any[]) {
    this.documentationRest.updateMediaOrder(articleCode, orderArray).subscribe(
      (result) => {},
      (error) => {
        console.log(error);
      }
    );
  }
}
